import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { VisitRecord } from '@/lib/visit-service';

interface EditVisitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visit: VisitRecord | null;
  onVisitUpdate: (updates: { notes?: string; visit_type?: string; location?: string }) => void;
  isUpdating: boolean;
}

export const EditVisitModal = ({ 
  open, 
  onOpenChange, 
  visit,
  onVisitUpdate, 
  isUpdating 
}: EditVisitModalProps) => {
  const [notes, setNotes] = useState('');
  const [visitType, setVisitType] = useState('');
  const [location, setLocation] = useState('');

  // Initialize form when visit data is available
  useEffect(() => {
    if (visit && open) {
      setNotes(visit.notes || '');
      setVisitType(visit.visit_type || '');
      setLocation(visit.location || '');
    }
  }, [visit, open]);

  const handleSubmit = () => {
    const updates: { notes?: string; visit_type?: string; location?: string } = {};
    
    if (notes !== (visit?.notes || '')) {
      updates.notes = notes;
    }
    if (visitType !== (visit?.visit_type || '')) {
      updates.visit_type = visitType;
    }
    if (location !== (visit?.location || '')) {
      updates.location = location;
    }

    // Only update if there are actual changes
    if (Object.keys(updates).length > 0) {
      onVisitUpdate(updates);
    } else {
      onOpenChange(false);
    }
  };

  const resetForm = () => {
    if (visit) {
      setNotes(visit.notes || '');
      setVisitType(visit.visit_type || '');
      setLocation(visit.location || '');
    }
  };

  const visitTypes = [
    { value: '', label: 'Belirtilmemiş' },
    { value: 'inceledi', label: 'Ürünü İnceledi' },
    { value: 'fiyat_talep', label: 'Fiyat Talep Etti' },
    { value: 'teklif_verildi', label: 'Teklif Verildi' },
    { value: 'satis_yapildi', label: 'Satış Yapıldı' },
    { value: 'danisman', label: 'Danışman Görüşmesi' },
    { value: 'servis', label: 'Servis Talebi' },
  ];

  const locations = [
    'Salon A',
    'Salon B', 
    'Salon C',
    'Depo',
    'Ofis',
    'Online',
    'Müşteri Mekanı',
    'Diğer'
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ziyaret Düzenle</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Visit Type */}
          <div className="space-y-2">
            <Label>Ziyaret Türü</Label>
            <Select value={visitType} onValueChange={setVisitType}>
              <SelectTrigger>
                <SelectValue placeholder="Ziyaret türü seçin" />
              </SelectTrigger>
              <SelectContent>
                {visitTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label>Lokasyon</Label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger>
                <SelectValue placeholder="Lokasyon seçin" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc} value={loc}>
                    {loc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Ziyaret Notları</Label>
            <Textarea
              placeholder="Ziyaret detayları, müşteri yorumları..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              İptal
            </Button>
            <Button variant="outline" onClick={resetForm} disabled={isUpdating}>
              Sıfırla
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isUpdating}
              className="flex-1"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Güncelleniyor...
                </>
              ) : (
                'Güncelle'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
